import React from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { colors, spacing } from '../../../theme';
import { CustomTemplate, HeaderAsset } from '../types/messageForm.types';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

interface WhatsAppMessagePreviewProps {
  template?: CustomTemplate | null;
  customMessage?: string;
  templateParameters?: Record<number, string>;
  headerAsset?: HeaderAsset;
  businessName?: string;
}

type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'strike'; text: string };

// Splits text into segments: literal strings, {{n}} params, *bold*, ~strike~
function parseSegments(text: string, params: Record<number, string>): Segment[] {
  // First, replace all {{n}} with their parameter values
  let processedText = text.replace(/\{\{(\d+)\}\}/g, (match, posStr) => {
    const pos = parseInt(posStr, 10);
    const value = params[pos];
    return value || `[param ${pos}]`;
  });

  // Now parse the processed text for formatting (bold, strike)
  const parts: Segment[] = [];
  const regex = /\*([^*]+)\*|~([^~]+)~/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(processedText)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ kind: 'text', text: processedText.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      parts.push({ kind: 'bold', text: match[1] });
    } else if (match[2] !== undefined) {
      parts.push({ kind: 'strike', text: match[2] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < processedText.length) {
    parts.push({ kind: 'text', text: processedText.slice(lastIndex) });
  }

  return parts;
}

const RichText: React.FC<{ text: string; params: Record<number, string>; style: any }> = ({
  text,
  params,
  style,
}) => {
  const styles = useThemedStyles(getStyles);
  const segments = parseSegments(text, params);
  return (
    <Text style={style}>
      {segments.map((seg, i) => {
        if (seg.kind === 'bold') return <Text key={i} style={styles.boldText}>{seg.text}</Text>;
        if (seg.kind === 'strike') return <Text key={i} style={styles.strikeText}>{seg.text}</Text>;
        return <Text key={i}>{seg.text}</Text>;
      })}
    </Text>
  );
};

export const WhatsAppMessagePreview: React.FC<WhatsAppMessagePreviewProps> = ({
  template,
  customMessage,
  templateParameters = {},
  headerAsset,
  businessName = 'Your Business',
}) => {
  const styles = useThemedStyles(getStyles);
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  const timeStr = `${h12}:${m} ${ampm}`;

  const hasContent = !!(template || customMessage);

  return (
    <View style={styles.outerContainer}>
      {/* Mock WhatsApp header bar */}
      <View style={styles.chatHeader}>
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarLetter}>B</Text>
        </View>
        <View style={styles.chatHeaderText}>
          <Text style={styles.chatName}>{businessName}</Text>
          <Text style={styles.chatOnline}>WhatsApp Business</Text>
        </View>
      </View>

      {/* Wallpaper area */}
      <View style={styles.wallpaper}>
        <View style={styles.bubbleWrapper}>
          <View style={styles.bubble}>
            {/* Header image */}
            {headerAsset?.url ? (
              <Image source={{ uri: headerAsset.url }} style={styles.headerImage} resizeMode="cover" />
            ) : null}

            {/* Text header (bold) – only when no image */}
            {template?.templateHeader && !headerAsset?.url ? (
              <RichText
                text={template.templateHeader}
                params={templateParameters}
                style={styles.headerText}
              />
            ) : null}

            {/* Body */}
            {template ? (
              <RichText text={template.templateBody} params={templateParameters} style={styles.bodyText} />
            ) : customMessage ? (
              <Text style={styles.bodyText}>{customMessage}</Text>
            ) : (
              <Text style={styles.placeholderText}>Your message will appear here…</Text>
            )}

            {/* Footer */}
            {template?.templateFooter ? (
              <RichText
                text={template.templateFooter}
                params={templateParameters}
                style={styles.footerText}
              />
            ) : null}

            {/* Timestamp row */}
            <View style={styles.timestampRow}>
              <Text style={styles.timestamp}>{timeStr}</Text>
              <Text style={styles.ticks}> ✓✓</Text>
            </View>
          </View>

          {/* Bubble tail (top-right corner) */}
          <View style={styles.bubbleTail} />
        </View>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  outerContainer: {
    borderRadius: spacing.borderRadius.lg,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.borderMedium,
    backgroundColor: '#fff',
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#075E54',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 10,
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#128C7E',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarLetter: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 16,
  },
  chatHeaderText: {
    flex: 1,
  },
  chatName: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 15,
  },
  chatOnline: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 12,
  },
  wallpaper: {
    backgroundColor: '#ECE5DD',
    padding: 16,
    minHeight: 100,
  },
  bubbleWrapper: {
    alignSelf: 'flex-end',
    maxWidth: '95%',
    position: 'relative',
  },
  bubble: {
    backgroundColor: '#DCF8C6',
    borderRadius: 12,
    borderTopRightRadius: 2,
    padding: 10,
    paddingBottom: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  bubbleTail: {
    position: 'absolute',
    top: 0,
    right: -8,
    width: 0,
    height: 0,
    borderTopWidth: 10,
    borderLeftWidth: 10,
    borderTopColor: '#DCF8C6',
    borderLeftColor: 'transparent',
  },
  headerImage: {
    width: '100%',
    height: 150,
    borderRadius: 8,
    marginBottom: 8,
  },
  headerText: {
    fontWeight: '700',
    fontSize: 14,
    color: '#111',
    marginBottom: 5,
    lineHeight: 20,
  },
  bodyText: {
    fontSize: 14,
    color: '#111',
    lineHeight: 20,
  },
  footerText: {
    fontSize: 12,
    color: '#667781',
    marginTop: 4,
    lineHeight: 16,
  },
  placeholderText: {
    fontSize: 13,
    color: '#aaa',
    fontStyle: 'italic',
  },
  timestampRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 4,
  },
  timestamp: {
    fontSize: 11,
    color: '#667781',
  },
  ticks: {
    fontSize: 11,
    color: '#53BDEB',
    fontWeight: '600',
  },
  missingParam: {
    backgroundColor: '#FFF3CD',
    color: '#856404',
    borderRadius: 3,
    overflow: 'hidden',
  },
  boldText: {
    fontWeight: '700',
  },
  strikeText: {
    textDecorationLine: 'line-through',
  },
});
